import { IDecryptNode, IPlainNodeState } from '@/contracts/modules/libre/use-case/IDecryptNode'
import { INodeState } from '@/contracts/modules/libre/use-case/IFoldNode'
import { ILibreCipher } from '@/contracts/modules/libre/cipher/ILibreCipher'
import { ILibrePlace } from '@/contracts/modules/libre/entities/ILibrePlace'
import { ILibreData } from '@/contracts/modules/libre/entities/ILibreData'
import { EnumLibrePart } from '@/contracts/modules/libre/enum/EnumLibrePart'
import { EnumLibreError } from '@/contracts/modules/libre/enum/EnumLibreError'
import { isLibreKind } from '@/contracts/modules/libre/enum/EnumLibreKind'
import { LibreCipher } from '@/modules/libre/v1/cipher/LibreCipher'

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function isPlace(value: unknown): value is ILibrePlace {
  const place = value as ILibrePlace
  return typeof value === 'object' && value !== null
    && (place.parent === null || typeof place.parent === 'string')
    && typeof place.position === 'string'
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function isStringMap(value: unknown): value is Record<string, string> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    && Object.values(value as Record<string, unknown>).every((item) => typeof item === 'string')
}

function isData(value: unknown): value is ILibreData {
  const data = value as ILibreData
  return typeof value === 'object' && value !== null
    && isLibreKind(data.kind)
    && isStringList(data.tags)
    && typeof data.content === 'string'
    && isStringMap(data.attrs)
    && typeof data.actualFrom === 'string' && DATE_PATTERN.test(data.actualFrom)
    && (data.actualTo === null || (typeof data.actualTo === 'string' && DATE_PATTERN.test(data.actualTo)))
}

/** Расшифровывает место и данные после свёртки; расшифрованное не той формы — cannot_decrypt */
export class DecryptNode implements IDecryptNode {
  constructor(private cipher: ILibreCipher = new LibreCipher()) {}

  async exec(state: INodeState, key: CryptoKey): Promise<IPlainNodeState> {
    const place = this.parse(await this.cipher.decrypt(key, EnumLibrePart.PLACE, state.place))
    const data = this.parse(await this.cipher.decrypt(key, EnumLibrePart.DATA, state.data))
    if (!isPlace(place) || !isData(data)) {
      throw new Error(EnumLibreError.CANNOT_DECRYPT)
    }

    return { place, data, isRemoved: state.isRemoved, updatedAt: state.updatedAt }
  }

  private parse(text: string): unknown {
    try {
      return JSON.parse(text)
    } catch {
      throw new Error(EnumLibreError.CANNOT_DECRYPT)
    }
  }
}
